import { PotDetails, PotSummary, CurrentPotDetails } from '../pot/PotDetails';
import { Link } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { Avatar, Button, Descriptions, Empty, Drawer, Input, Pagination, Result, Select, Space, Spin, Switch, Tooltip, Table, Tabs, Tag, theme, Typography } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { PoolInfoPanel } from './PoolInfoPanel';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, FileTextOutlined, ReloadOutlined } from '@ant-design/icons';
import { formatDateTime } from '../../utils/format';
import { usePoolInfo, useGameServerRooms, useGameServerLogs, useGameServerMatchDetail, useGameServerMatches, useGameServers } from './queries';
import { GameRoom, GameServer, GameServerMatch, GameServerState, MatchParticipant } from './types';

import { RoomView } from './RoomView';
import { roomState } from './room-layout';
import './game-servers.css';
import { ServerListTable, STATE_COLORS } from './ServerListTable';

const { Text, Title, Paragraph } = Typography;

const formatNumber = (value: number | undefined) =>
  value === undefined || value === null ? '—' : value.toLocaleString();

const MatchRows: React.FC<{
  serverID: string;
  onView: (match: GameServerMatch) => void;
}> = ({ serverID, onView }) => {
  const [page, setPage] = useState(1);
  const { data, error, isLoading, isPlaceholderData, refetch } =
    useGameServerMatches(serverID, page);

  if (error) {
    return (
      <Result
        status="error"
        title="Failed to load rounds"
        subTitle={error instanceof Error ? error.message : 'An unexpected error occurred'}
        extra={<Button onClick={() => refetch()}>Retry</Button>}
      />
    );
  }

  const columns: ColumnsType<GameServerMatch> = [
    { title: 'Room', dataIndex: 'external_room_id', key: 'external_room_id', render: (value) => <code>{value}</code> },
    { title: 'Round', dataIndex: 'match_number', key: 'match_number', width: 90 },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: (value: GameServerMatch['status']) => <Tag color={value === 'playing' ? 'blue' : 'green'}>{value.toUpperCase()}</Tag>,
    },
    { title: 'Pot', key: 'pot', render: (_, match) => <PotSummary pot={match.pot} /> },
    { title: 'Humans', dataIndex: 'human_count', key: 'human_count', width: 90 },
    { title: 'Bots', dataIndex: 'bot_count', key: 'bot_count', width: 70 },
    { title: 'Joined', dataIndex: 'joined_at', key: 'joined_at', render: formatDateTime },
    { title: 'Ended', dataIndex: 'end_at', key: 'end_at', render: formatDateTime },
    {
      title: '', key: 'view', width: 70,
      render: (_, match) => <Button type="link" icon={<EyeOutlined />} onClick={() => onView(match)}>View</Button>,
    },
  ];

  return (
    <Table
      size="small"
      rowKey={(match) => `${match.room_id}:${match.match_number}`}
      columns={columns}
      dataSource={data?.data ?? []}
      loading={isLoading}
      scroll={{ x: 850 }}
      pagination={{
        current: data?.meta.page ?? page,
        pageSize: 10,
        total: data?.meta.total ?? 0,
        size: 'small',
        showSizeChanger: false,
        onChange: setPage,
        disabled: isPlaceholderData,
      }}
    />
  );
};

const ParticipantTable: React.FC<{ participants: MatchParticipant[] }> = ({ participants }) => {
  const columns: ColumnsType<MatchParticipant> = [
    { title: 'Type', dataIndex: 'type', key: 'type', render: (value) => <Tag color={value === 'bot' ? 'purple' : 'blue'}>{value.toUpperCase()}</Tag> },
    { title: 'Player', key: 'player', render: (_, participant) => participant.type !== 'bot' && participant.user_id ? <Link to={`/users/${participant.user_id}`}>{participant.username || participant.user_id}</Link> : participant.username || (participant.bot_slot ? `Bot slot ${participant.bot_slot}` : '—') },
    { title: 'Skill', dataIndex: 'bot_skill', key: 'bot_skill', render: (value) => value || '—' },
    { title: 'Bet', dataIndex: 'bet', key: 'bet', render: formatNumber },
    { title: 'Rank', dataIndex: 'rank', key: 'rank', render: formatNumber },
    { title: 'Score', dataIndex: 'score', key: 'score', render: formatNumber },
    { title: 'Gold net', dataIndex: 'net_gold_delta', key: 'net_gold_delta', render: formatNumber },
    { title: 'Gem Δ', dataIndex: 'gem_delta', key: 'gem_delta', render: formatNumber },
    { title: 'EXP', dataIndex: 'exp_gained', key: 'exp_gained', render: formatNumber },
  ];
  return <Table size="small" rowKey={(p, index) => `${p.type}:${p.user_id ?? p.bot_slot ?? index}`} columns={columns} dataSource={participants} scroll={{ x: 850 }} pagination={false} />;
};

const MatchDetailDrawer: React.FC<{
  serverID?: string;
  match?: GameServerMatch;
  onClose: () => void;
}> = ({ serverID, match, onClose }) => {
  const { data, error, isLoading } = useGameServerMatchDetail(serverID, match?.room_id, match?.match_number);
  return (
    <Drawer title="Match detail" width={960} open={Boolean(match)} onClose={onClose} destroyOnClose>
      {error ? <Result status="error" title="Failed to load match detail" subTitle={error instanceof Error ? error.message : 'An unexpected error occurred'} /> : (
        <>
          {data && <Descriptions size="small" bordered column={{ xs: 1, sm: 2 }} items={[
            { key: 'room', label: 'Room', children: <code>{data.external_room_id}</code> },
            { key: 'round', label: 'Round', children: data.match_number },
            { key: 'status', label: 'Status', children: <Tag color={data.status === 'playing' ? 'blue' : 'green'}>{data.status.toUpperCase()}</Tag> },
            { key: 'joined', label: 'Joined', children: formatDateTime(data.joined_at) },
            { key: 'ended', label: 'Ended', children: formatDateTime(data.end_at) },
          ]} />}
          {data && <PotDetails pot={data.pot} />}
          <div className="mt-5"><ParticipantTable participants={data?.participants ?? []} /></div>
          {isLoading && <Text type="secondary">Loading match detail…</Text>}
        </>
      )}
    </Drawer>
  );
};

const ServerLogsDrawer: React.FC<{ serverID?: string; onClose: () => void }> = ({ serverID, onClose }) => {
  const { token } = theme.useToken();
  const { data, error, isLoading, isFetching, refetch } = useGameServerLogs(serverID);
  return (
    <Drawer title={serverID ? `Container logs — ${serverID}` : 'Container logs'} width={960} open={Boolean(serverID)} onClose={onClose} destroyOnClose
      extra={<Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>Refresh</Button>}>
      {error ? <Result status="error" title="Failed to load container logs" subTitle={error instanceof Error ? error.message : 'An unexpected error occurred'} /> : (
        <pre style={{ margin: 0, padding: 16, minHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap', background: token.colorFillAlter, color: token.colorText, borderRadius: token.borderRadius }}>
          {isLoading ? 'Loading logs…' : data || 'No logs available.'}
        </pre>
      )}
    </Drawer>
  );
};

const ServerFloor: React.FC<{ server: GameServer; autoRefresh: boolean; onLogs: () => void }> = ({ server, autoRefresh, onLogs }) => {
  const { token } = theme.useToken();
  const [page, setPage] = useState(1);
  const [selectedID, setSelectedID] = useState<number>();
  const [history, setHistory] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<GameServerMatch>();
  const { data, error, isLoading, isFetching, dataUpdatedAt, refetch } = useGameServerRooms(server.id, page, autoRefresh);
  useEffect(() => {
    if (data && page > Math.max(1, data.meta.total_pages)) {
      setPage(Math.max(1, data.meta.total_pages));
      setSelectedID(undefined);
    }
  }, [data, page]);
  const rooms = data?.data ?? [];
  const selected = rooms.find((room) => room.room_id === selectedID);
  const humans = rooms.reduce((sum, room) => sum + room.players.filter((p) => p.status !== 'reserved' && p.status !== 'disconnect').length, 0);
  const bots = rooms.reduce((sum, room) => sum + room.bot_count, 0);
  const round = selected?.players.find((p) => p.status === 'playing' && p.match_number)?.match_number;

  const viewCurrentMatch = (room: GameRoom, matchNumber: number) => setSelectedMatch({
    room_id: room.room_id, external_room_id: room.external_room_id, match_number: matchNumber,
    status: 'playing', joined_at: '', human_count: room.players.length, bot_count: room.bot_count,
  });

  return <>
    <div className="floor-server-heading">
      <Space wrap className="server-info-metrics">
        <Tag color={STATE_COLORS[server.state]}>{server.state}</Tag>
        <Text strong copyable className="server-info-id">{server.id}</Text>
        <Tooltip title="Capacity reported by this server; may differ from max tables for new containers. INIT has not reported readiness yet."><Text>Capacity: <strong>{server.state === 'INIT' ? '—' : server.capacity ?? '—'}</strong></Text></Tooltip>
        <Tooltip title="Occupied table slots recorded by the pool, including reservations; not the number of tables on this floor page."><Text>In use: <strong>{server.active_rooms ?? '—'} / {server.state === 'INIT' ? '—' : server.capacity ?? '—'}</strong></Text></Tooltip>
        <Tooltip title="Confirmed players on the visible floor page; excludes reserved and disconnected memberships."><Text>Players: <strong>{data && !error ? humans : '—'}</strong></Text></Tooltip>
        <Tooltip title="Bots on the visible floor page."><Text>Bots: <strong>{data && !error ? bots : '—'}</strong></Text></Tooltip>
        {server.ttl_seconds !== undefined && <Text>TTL: <strong>{server.ttl_seconds}s</strong></Text>}
      </Space>
      {(server.rotation_replacement_id || server.rotation_source_id) && <Descriptions size="small" column={{ xs: 1, sm: 2 }} items={[
        ...(server.rotation_replacement_id ? [{ key: 'replacement', label: 'Replacement server', children: <Text code copyable>{server.rotation_replacement_id}</Text> }] : []),
        ...(server.rotation_source_id ? [{ key: 'source', label: 'Rotation source', children: <Text code copyable>{server.rotation_source_id}</Text> }] : []),
      ]} />}
      <Space wrap><Button onClick={() => setHistory(true)}>Round history</Button><Button icon={<FileTextOutlined />} onClick={onLogs}>Logs</Button>
        <Tooltip title={dataUpdatedAt ? `Floor updated ${formatDateTime(dataUpdatedAt)}` : 'Refresh floor'}><Button aria-label="Refresh floor" icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()} /></Tooltip>
      </Space>
    </div>
    {error ? <Result status="error" title="Could not load the floor" subTitle={error instanceof Error ? error.message : 'Please try again.'} extra={<Button onClick={() => refetch()}>Retry</Button>} /> :
      isLoading ? <div className="floor-loading"><Spin /><p>Opening the room…</p></div> :
      <div className="floor-body">
        <RoomView rooms={rooms} selectedID={selectedID} onSelect={(room) => setSelectedID(room.room_id)} />
        <aside className="floor-inspector">
          {selected ? <>
            <Title level={4}>Table {selected.room_id}</Title>
            <Paragraph type="secondary">{selected.name || 'Tongits'}</Paragraph>
            <div><Tag color={roomState(selected) === 'Playing' ? 'success' : roomState(selected) === 'Preparing' ? 'warning' : 'default'}>{roomState(selected)}</Tag></div>
            <Descriptions size="small" column={1} layout="vertical" items={[
              { key: 'id', label: 'Table ID', children: <Text code className="floor-id">{selected.external_room_id}</Text> },
              { key: 'bet', label: 'Buy-in / bet', children: `${selected.bet.toLocaleString()} gold` },
              { key: 'admission', label: 'Admission', children: <Tag color={selected.is_joinable ? 'success' : 'default'}>{selected.is_joinable ? 'Open' : 'Closed'}</Tag> },
            ]} />
            <CurrentPotDetails protocol={selected.pot_protocol} state={selected.pot_state} />
            <Title level={5}>At the table</Title>
            {selected.players.map((player) => <div className="floor-person" key={player.user_id}>
              <Avatar style={{ backgroundColor: token.colorPrimary }}>{(player.username || 'P').slice(0, 2).toUpperCase()}</Avatar>
              <div><Link to={`/users/${player.user_id}`}>{player.username || `Player ${player.user_id}`}</Link><br /><Text type="secondary">{player.status} · </Text><Link to={`/users/${player.user_id}`}>#{player.user_id}</Link></div>
            </div>)}
            {selected.bot_count > 0 && <div className="floor-person"><Avatar style={{ backgroundColor: token.colorWarning }}>B</Avatar><div><Text strong>{selected.bot_count} {selected.bot_count === 1 ? 'bot' : 'bots'}</Text><br /><Text type="secondary">Automated players</Text></div></div>}
            {!selected.players.length && !selected.bot_count && <Text type="secondary">No occupied seats.</Text>}
            {round && <Button block onClick={() => viewCurrentMatch(selected, round)}>View current round #{round}</Button>}
          </> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a table to view its players, stakes and status." />}
          <Paragraph type="secondary" className="floor-note">Seat ownership from the backend. Positions are schematic; connection state may lag game-server events.</Paragraph>
        </aside>
      </div>}
    <div className="floor-pagination"><span>12 tables per floor page</span><Pagination current={page} pageSize={12} total={data?.meta.total ?? 0} showSizeChanger={false} disabled={isFetching} onChange={(next) => { setPage(next); setSelectedID(undefined); }} /></div>
    <Drawer title={`Round history · ${server.id}`} width={960} open={history} onClose={() => setHistory(false)} destroyOnClose><MatchRows serverID={server.id} onView={setSelectedMatch} /></Drawer>
    <MatchDetailDrawer serverID={server.id} match={selectedMatch} onClose={() => setSelectedMatch(undefined)} />
  </>;
};

export const GameServersPage: React.FC = () => {
  const { token } = theme.useToken();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("rooms");
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<GameServerState | undefined>();
  const [id, setID] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const pool = usePoolInfo(autoRefresh);
  const [selectedServerID, setSelectedServerID] = useState<string>();
  const [logServerID, setLogServerID] = useState<string>();
  const { data, error, isLoading, isFetching, isPlaceholderData, refetch } =
    useGameServers(page, 20, status, id, autoRefresh);
  useEffect(() => {
    if (!data || isPlaceholderData) return;
    if (page > Math.max(1, data.meta.total_pages)) {
      setPage(Math.max(1, data.meta.total_pages));
      return;
    }
    if (!data.data.some((server) => server.id === selectedServerID)) {
      setSelectedServerID(data.data[0]?.id);
    }
  }, [data, isPlaceholderData, page, selectedServerID]);
  const servers = data?.data ?? [];
  const selectedServer =
    servers.find((server) => server.id === selectedServerID) ?? servers[0];
  const searchByID = (value: string) => {
    setID(value.trim());
    setPage(1);
    setSelectedServerID(undefined);
  };

  return (
    <div
      className="game-floor"
      style={
        {
          "--floor-bg": token.colorBgContainer,
          "--floor-surface": token.colorFillAlter,
          "--floor-text": token.colorText,
          "--floor-muted": token.colorTextSecondary,
          "--floor-border": token.colorBorderSecondary,
          "--floor-primary": token.colorPrimary,
          "--floor-primary-bg": token.colorPrimaryBg,
          "--floor-success": token.colorSuccess,
          "--floor-success-bg": token.colorSuccessBg,
          "--floor-warning": token.colorWarning,
          "--floor-warning-text": token.colorWarningText,
          "--floor-warning-bg": token.colorWarningBg,
          "--floor-white": token.colorTextLightSolid,
          borderRadius: token.borderRadiusLG,
          fontFamily: token.fontFamily,
        } as React.CSSProperties
      }
    >
      <section
        aria-labelledby="pool-info-heading"
        className="pool-info-section"
      >
        <header className="game-floor-header">
          <Title level={4} id="pool-info-heading">
            Pool info
          </Title>
          <Space>
            <Switch
              aria-label="Auto refresh every 5 seconds"
              checked={autoRefresh}
              onChange={setAutoRefresh}
            />
            <Text>{autoRefresh ? "Refresh every 5s" : "Refresh paused"}</Text>
            <Tooltip title="Refresh pool configuration and game server data">
              <Button
                aria-label="Refresh all"
                icon={<ReloadOutlined />}
                loading={isFetching || pool.isFetching}
                onClick={() => {
                  pool.refetch();
                  refetch();
                  queryClient.invalidateQueries({
                    queryKey: ["game-server-rooms"],
                  });
                }}
              />
            </Tooltip>
          </Space>
        </header>
        <PoolInfoPanel
          data={pool.data}
          loading={pool.isLoading}
          error={pool.error}
          onRetry={() => pool.refetch()}
          onViewDraining={() => {
            setStatus("DRAINING");
            setPage(1);
            setSelectedServerID(undefined);
          }}
        />
      </section>
      <section aria-labelledby="game-server-info-heading">
        <Title
          level={4}
          id="game-server-info-heading"
          className="server-info-title"
        >
          Game server info
        </Title>
        <Tabs
          className="game-server-tabs"
          activeKey={tab}
          onChange={setTab}
          items={[
            { key: "rooms", label: "Room view" },
            { key: "servers", label: "Server list" },
          ]}
        />
        <div className="floor-toolbar">
          <Input.Search
            aria-label="Search container ID"
            placeholder="Find a server…"
            maxLength={255}
            allowClear
            onSearch={searchByID}
            onChange={(event) => {
              if (!event.target.value) searchByID("");
            }}
            style={{ width: 235 }}
          />
          <Select
            aria-label="Server state"
            allowClear
            placeholder="All server states"
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
              setSelectedServerID(undefined);
            }}
            options={Object.keys(STATE_COLORS).map((value) => ({
              value,
              label: value,
            }))}
            style={{ width: 165 }}
          />
          {tab === "rooms" && (
            <Select
              aria-label="Select server"
              value={selectedServer?.id}
              placeholder="Select a server"
              onChange={setSelectedServerID}
              disabled={isPlaceholderData}
              options={servers.map((server) => ({
                value: server.id,
                label: `${server.state} · ${server.id}`,
              }))}
              style={{ minWidth: 220, flex: 1 }}
            />
          )}
          {tab === "rooms" && (data?.meta.total ?? 0) > 20 && (
            <Pagination
              aria-label="Server pages"
              simple
              size="small"
              current={page}
              pageSize={20}
              total={data?.meta.total}
              showSizeChanger={false}
              disabled={isFetching}
              onChange={(next) => {
                setPage(next);
                setSelectedServerID(undefined);
              }}
            />
          )}
        </div>

        {error ? (
          <Result
            status="error"
            title="Failed to load game servers"
            subTitle={
              error instanceof Error ? error.message : "Please try again."
            }
            extra={<Button onClick={() => refetch()}>Retry</Button>}
          />
        ) : tab === "servers" ? (
          <ServerListTable
            servers={servers}
            page={page}
            total={data?.meta.total ?? 0}
            loading={isLoading || isPlaceholderData}
            onPageChange={(next) => {
              setPage(next);
              setSelectedServerID(undefined);
            }}
            onRooms={(serverID) => {
              setSelectedServerID(serverID);
              setTab("rooms");
            }}
            onLogs={setLogServerID}
          />
        ) : isLoading || isPlaceholderData ? (
          <div className="floor-loading">
            <Spin />
            <p>Finding game servers…</p>
          </div>
        ) : selectedServer ? (
          <ServerFloor
            key={selectedServer.id}
            server={selectedServer}
            autoRefresh={autoRefresh}
            onLogs={() => setLogServerID(selectedServer.id)}
          />
        ) : (
          <div className="floor-loading">
            <Empty description="No servers found. Try another server ID or state." />
          </div>
        )}
      </section>
      <ServerLogsDrawer
        serverID={logServerID}
        onClose={() => setLogServerID(undefined)}
      />
    </div>
  );
};
